<template>
  <QCarousel
    v-model="slide"
    animated
    infinite
    autoplay
    :swipeable="swipeable"
    transition-prev="slide-right"
    transition-next="slide-left"
    class="banner-carousel"
  >
    <QCarouselSlide
      v-for="(banner, index) in modelValue"
      :key="`banner-${banner._id}`"
      :name="banner._id"
      class="tw-p-0"
    >
      <!-- Desktop Image -->
      <img
        :src="banner.desktopImage.large"
        :srcset="buildSrcset(banner.desktopImage)"
        sizes="(max-width: 767px) 0px, 100vw"
        :alt="banner.title"
        :class="{ 'tw-cursor-pointer': banner.link }"
        class="tw-h-full tw-hidden md:tw-block tw-w-full tw-object-cover"
        :fetchpriority="index === 0 ? 'high' : undefined"
        :loading="index === 0 ? undefined : 'lazy'"
        :decoding="index === 0 ? 'sync' : 'async'"
        width="1920"
        height="822"
        @click="() => onClickMedia(banner)"
      />
      <!-- Mobile Image -->
      <img
        :src="banner.mobileImage.large"
        :srcset="buildSrcset(banner.mobileImage)"
        sizes="(min-width: 768px) 0px, 100vw"
        :alt="banner.title"
        :class="{ 'tw-cursor-pointer': banner.link }"
        class="tw-h-full md:tw-hidden tw-w-full tw-object-cover"
        :fetchpriority="index === 0 ? 'high' : undefined"
        :loading="index === 0 ? undefined : 'lazy'"
        :decoding="index === 0 ? 'sync' : 'async'"
        width="375"
        height="375"
        @click="() => onClickMedia(banner)"
      />
    </QCarouselSlide>
  </QCarousel>
</template>

<script setup lang="ts">
const props = defineProps({
  modelValue: {
    type: Array as PropType<Banner[]>,
    required: true,
  },
  swipeable: {
    type: Boolean,
    default: false,
  },
})
const slide = ref(props.modelValue?.[0]?._id)

defineExpose({
  activeSlide: computed(() => slide.value),
  goToSlide: (id: string) => { slide.value = id },
})

const buildSrcset = (image: ImageMeta): string => {
  const parts: string[] = []
  if (image.small) parts.push(`${image.small} 550w`)
  if (image.medium) parts.push(`${image.medium} 900w`)
  if (image.large) parts.push(`${image.large} 1920w`)
  return parts.join(', ')
}

const onClickMedia = (banner: Banner) => {
  if (banner.link) {
    window.open(banner.link, '_blank')
  }
}
</script>

<style>
/* Mobile / tablet: full-width square (1:1) — no max-height so 430px and 768px
   viewports render square, not capped at 375px */
.banner-carousel {
  height: 100vw !important;
}

@media (max-width: 767px) {
  .banner-carousel .q-carousel__arrow--left {
    left: 16px !important;
  }

  .banner-carousel .q-carousel__arrow--right {
    right: 16px !important;
  }

  .banner-carousel .q-carousel__arrow .q-btn {
    width: 20px !important;
    height: 20px !important;
    min-width: 20px !important;
    min-height: 20px !important;
    padding: 0 !important;
  }

  .banner-carousel .q-carousel__navigation {
    bottom: 16px !important;
  }
}

/* Desktop: match the backoffice crop boundary (1920x822). */
@media (min-width: 768px) {
  .banner-carousel {
    height: calc(100vw * 822 / 1920) !important;
    max-height: 822px !important;
  }
}
</style>
